import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft, Download, UserCheck } from 'lucide-react'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { StatusBadge } from '@/components/hr/status-badge'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Separator } from '@/components/ui/separator'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

// ── Types ─────────────────────────────────────────────────────────────────────

type Employee = {
  id: number
  firstName: string
  lastName: string
}

type Interview = {
  id: number
  interviewerId: number | null
  interviewerFirstName: string | null
  interviewerLastName: string | null
  scheduledAt: string | Date
  durationMinutes: number | null
  type: string
  status: string
  feedback: string | null
  rating: number | null
}

type OfferLetter = {
  id: number
  salary: string
  startDate: string | Date
  status: string
  validUntil: string | Date | null
}

type ApplicantDetail = {
  id: number
  openingId: number
  openingTitle: string | null
  firstName: string
  lastName: string
  email: string
  phone: string | null
  resumeUrl: string | null
  coverLetter: string | null
  source: string | null
  status: string
  rating: number | null
  notes: string | null
  interviews: Interview[]
}

// ── Schemas ───────────────────────────────────────────────────────────────────

const scheduleInterviewSchema = z.object({
  interviewerId: z.number().min(1, 'Interviewer is required'),
  scheduledAt: z.string().min(1, 'Date and time is required'),
  durationMinutes: z.number().int().min(15),
  type: z.enum(['phone', 'video', 'onsite', 'technical', 'cultural']),
})

const createOfferSchema = z.object({
  salary: z.number().positive('Salary is required'),
  startDate: z.string().min(1, 'Start date is required'),
  validUntil: z.string().optional(),
  templateContent: z.string().optional(),
})

type ScheduleInterviewFormValues = z.infer<typeof scheduleInterviewSchema>
type CreateOfferFormValues = z.infer<typeof createOfferSchema>

// ── Constants ─────────────────────────────────────────────────────────────────

const sourceColors: Record<string, string> = {
  website: 'bg-blue-100 text-blue-800',
  referral: 'bg-green-100 text-green-800',
  linkedin: 'bg-indigo-100 text-indigo-800',
  agency: 'bg-purple-100 text-purple-800',
  other: 'bg-gray-100 text-gray-800',
}

const interviewTypeColors: Record<string, string> = {
  phone: 'bg-blue-100 text-blue-800',
  video: 'bg-cyan-100 text-cyan-800',
  onsite: 'bg-green-100 text-green-800',
  technical: 'bg-orange-100 text-orange-800',
  cultural: 'bg-purple-100 text-purple-800',
}

// ── Route ─────────────────────────────────────────────────────────────────────

export const Route = createFileRoute('/hr/recruitment/applicants/$applicantId')({
  component: ApplicantDetailPage,
})

function ApplicantDetailPage() {
  const params = Route.useParams()
  const applicantId = Number(params.applicantId)
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const { data: applicantRaw, isLoading } = useQuery(
    orpc.hr.getApplicant.queryOptions({ input: { id: applicantId } }),
  )

  const { data: employeesData } = useQuery(
    orpc.hr.getEmployees.queryOptions({ input: { page: 1, limit: 500 } }),
  )

  const applicant = applicantRaw as ApplicantDetail | null
  const employees = (employeesData?.data ?? []) as Employee[]

  const [isScheduleOpen, setIsScheduleOpen] = useState(false)
  const [isOfferOpen, setIsOfferOpen] = useState(false)

  // Fetch offer letters for this applicant — use listApplicants indirectly via
  // the applicant's status. We infer offer from getApplicant extension if needed.
  // For simplicity, load the raw offer from a dedicated query by openingId + applicant.
  // The backend doesn't expose a listOfferLetters endpoint so we rely on applicant status
  // and embed the offer check inline. We'll track offer state via a simple local state
  // that gets populated when we know the applicant has status 'offer'.
  const [offerLetter, setOfferLetter] = useState<OfferLetter | null>(null)

  const createInterviewMutation = useMutation(
    orpc.hr.createInterview.mutationOptions({
      onSuccess: () => {
        toast.success('Interview scheduled')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getApplicant']] })
        setIsScheduleOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateInterviewMutation = useMutation(
    orpc.hr.updateInterview.mutationOptions({
      onSuccess: () => {
        toast.success('Interview updated')
        queryClient.invalidateQueries({ queryKey: [['hr', 'getApplicant']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const createOfferMutation = useMutation(
    orpc.hr.createOfferLetter.mutationOptions({
      onSuccess: (data) => {
        toast.success('Offer letter created')
        setOfferLetter(data as OfferLetter)
        queryClient.invalidateQueries({ queryKey: [['hr', 'getApplicant']] })
        setIsOfferOpen(false)
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const updateOfferStatusMutation = useMutation(
    orpc.hr.updateOfferLetterStatus.mutationOptions({
      onSuccess: (data) => {
        toast.success('Offer status updated')
        setOfferLetter(data as OfferLetter)
        queryClient.invalidateQueries({ queryKey: [['hr', 'getApplicant']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const hireMutation = useMutation(
    orpc.hr.hireApplicant.mutationOptions({
      onSuccess: (data) => {
        const result = data as { applicantId: number; employeeId: number }
        toast.success(
          `Applicant hired! Employee #${result.employeeId} created.`,
          {
            action: {
              label: 'View Employee',
              onClick: () => void navigate({ to: `/hr/employees/${result.employeeId}` }),
            },
          },
        )
        queryClient.invalidateQueries({ queryKey: [['hr', 'getApplicant']] })
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4">
        <Skeleton className="h-8 w-48 mb-6" />
        <Skeleton className="h-32 w-full mb-4" />
        <Skeleton className="h-48 w-full mb-4" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  if (!applicant) {
    return (
      <div className="container mx-auto py-6 px-4">
        <p className="text-muted-foreground">Applicant not found</p>
      </div>
    )
  }

  const canHire =
    applicant.status === 'offer' &&
    offerLetter?.status === 'accepted'

  return (
    <div className="container mx-auto py-6 px-4">
      <div className="mb-4 flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => window.history.back()}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back
        </Button>
      </div>

      {/* Header */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-start justify-between">
            <div>
              <CardTitle className="text-xl">
                {applicant.firstName} {applicant.lastName}
              </CardTitle>
              <div className="mt-2 space-y-1">
                <p className="text-sm text-muted-foreground">{applicant.email}</p>
                {applicant.phone && (
                  <p className="text-sm text-muted-foreground">{applicant.phone}</p>
                )}
                {applicant.openingTitle && (
                  <p className="text-sm text-muted-foreground">
                    Applying for: {applicant.openingTitle}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 mt-3">
                {applicant.source && (
                  <span
                    className={`text-xs px-2 py-0.5 rounded capitalize ${sourceColors[applicant.source] ?? 'bg-gray-100 text-gray-800'}`}
                  >
                    {applicant.source}
                  </span>
                )}
                {applicant.rating && (
                  <span className="text-sm text-yellow-500">
                    {'★'.repeat(applicant.rating)}{'☆'.repeat(5 - applicant.rating)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap justify-end">
              <StatusBadge status={applicant.status} />
              {applicant.resumeUrl && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(applicant.resumeUrl!, '_blank')}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Resume
                </Button>
              )}
              {canHire && (
                <Button
                  size="sm"
                  onClick={() => hireMutation.mutate({ id: applicant.id })}
                  disabled={hireMutation.isPending}
                >
                  <UserCheck className="h-4 w-4 mr-2" />
                  {hireMutation.isPending ? 'Hiring...' : 'Hire'}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        {applicant.coverLetter && (
          <CardContent>
            <Separator className="mb-4" />
            <h4 className="font-medium mb-2">Cover Letter</h4>
            <p className="text-sm text-muted-foreground whitespace-pre-wrap">
              {applicant.coverLetter}
            </p>
          </CardContent>
        )}
      </Card>

      {/* Interviews */}
      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Interviews</CardTitle>
            <Button size="sm" onClick={() => setIsScheduleOpen(true)}>
              Schedule Interview
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {applicant.interviews.length === 0 ? (
            <p className="text-sm text-muted-foreground">No interviews scheduled yet</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Interviewer</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Rating</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {applicant.interviews.map((interview) => (
                  <TableRow key={interview.id}>
                    <TableCell>
                      {new Date(interview.scheduledAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      {interview.interviewerFirstName
                        ? `${interview.interviewerFirstName} ${interview.interviewerLastName}`
                        : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge
                        className={`${interviewTypeColors[interview.type] ?? ''} capitalize`}
                        variant="outline"
                      >
                        {interview.type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {interview.durationMinutes ? `${interview.durationMinutes}m` : '-'}
                    </TableCell>
                    <TableCell>
                      <StatusBadge status={interview.status} />
                    </TableCell>
                    <TableCell>
                      {interview.rating ? (
                        <span className="text-yellow-500">
                          {'★'.repeat(interview.rating)}
                        </span>
                      ) : (
                        '-'
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      {interview.status === 'scheduled' && (
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateInterviewMutation.mutate({
                                id: interview.id,
                                status: 'completed',
                              })
                            }
                            disabled={updateInterviewMutation.isPending}
                          >
                            Complete
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-red-600"
                            onClick={() =>
                              updateInterviewMutation.mutate({
                                id: interview.id,
                                status: 'cancelled',
                              })
                            }
                            disabled={updateInterviewMutation.isPending}
                          >
                            Cancel
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Offer Letter */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Offer Letter</CardTitle>
            {!offerLetter && applicant.status !== 'hired' && (
              <Button size="sm" onClick={() => setIsOfferOpen(true)}>
                Create Offer
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {!offerLetter ? (
            <p className="text-sm text-muted-foreground">No offer letter yet</p>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Salary</p>
                  <p className="font-medium">
                    {Number(offerLetter.salary).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Start Date</p>
                  <p className="font-medium">
                    {new Date(offerLetter.startDate).toLocaleDateString()}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Valid Until</p>
                  <p className="font-medium">
                    {offerLetter.validUntil
                      ? new Date(offerLetter.validUntil).toLocaleDateString()
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <StatusBadge status={offerLetter.status} />
                </div>
              </div>
              {offerLetter.status === 'draft' && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      updateOfferStatusMutation.mutate({
                        id: offerLetter.id,
                        status: 'sent',
                      })
                    }
                    disabled={updateOfferStatusMutation.isPending}
                  >
                    Send Offer
                  </Button>
                </div>
              )}
              {offerLetter.status === 'sent' && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() =>
                      updateOfferStatusMutation.mutate({
                        id: offerLetter.id,
                        status: 'accepted',
                      })
                    }
                    disabled={updateOfferStatusMutation.isPending}
                  >
                    Mark Accepted
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-red-600"
                    onClick={() =>
                      updateOfferStatusMutation.mutate({
                        id: offerLetter.id,
                        status: 'rejected',
                      })
                    }
                    disabled={updateOfferStatusMutation.isPending}
                  >
                    Mark Rejected
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Schedule Interview Dialog */}
      <ScheduleInterviewDialog
        open={isScheduleOpen}
        onOpenChange={setIsScheduleOpen}
        employees={employees}
        onSubmit={async (data) => {
          await createInterviewMutation.mutateAsync({
            applicantId,
            interviewerId: data.interviewerId,
            scheduledAt: new Date(data.scheduledAt),
            durationMinutes: data.durationMinutes,
            type: data.type,
          })
        }}
      />

      {/* Create Offer Dialog */}
      <CreateOfferDialog
        open={isOfferOpen}
        onOpenChange={setIsOfferOpen}
        openingId={applicant.openingId}
        applicantId={applicantId}
        onSubmit={async (data) => {
          await createOfferMutation.mutateAsync({
            applicantId,
            openingId: applicant.openingId,
            salary: data.salary,
            startDate: new Date(data.startDate),
            validUntil: data.validUntil ? new Date(data.validUntil) : undefined,
            templateContent: data.templateContent,
          })
        }}
      />
    </div>
  )
}

// ── Schedule Interview Dialog ─────────────────────────────────────────────────

interface ScheduleInterviewDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  employees: Employee[]
  onSubmit: (data: ScheduleInterviewFormValues) => Promise<void>
}

function ScheduleInterviewDialog({
  open,
  onOpenChange,
  employees,
  onSubmit,
}: ScheduleInterviewDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<ScheduleInterviewFormValues>({
    resolver: zodResolver(scheduleInterviewSchema),
    defaultValues: {
      scheduledAt: '',
      durationMinutes: 60,
      type: 'video',
    },
  })

  const handleSubmit = async (data: ScheduleInterviewFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Schedule Interview</DialogTitle>
          <DialogDescription>Set up an interview for this applicant</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="interviewerId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Interviewer *</FormLabel>
                  <Select
                    onValueChange={(v) => field.onChange(Number(v))}
                    value={field.value?.toString() ?? ''}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select interviewer" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {employees.map((emp) => (
                        <SelectItem key={emp.id} value={emp.id.toString()}>
                          {emp.firstName} {emp.lastName}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="scheduledAt"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Date & Time *</FormLabel>
                  <FormControl>
                    <Input type="datetime-local" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="durationMinutes"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Duration (min)</FormLabel>
                    <FormControl>
                      <Input
                        type="number"
                        min={15}
                        max={480}
                        {...field}
                        onChange={(e) => field.onChange(Number(e.target.value))}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Type *</FormLabel>
                    <Select
                      onValueChange={(v) =>
                        field.onChange(
                          v as 'phone' | 'video' | 'onsite' | 'technical' | 'cultural',
                        )
                      }
                      value={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="phone">Phone</SelectItem>
                        <SelectItem value="video">Video</SelectItem>
                        <SelectItem value="onsite">Onsite</SelectItem>
                        <SelectItem value="technical">Technical</SelectItem>
                        <SelectItem value="cultural">Cultural</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Scheduling...' : 'Schedule'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

// ── Create Offer Dialog ───────────────────────────────────────────────────────

interface CreateOfferDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  openingId: number
  applicantId: number
  onSubmit: (data: CreateOfferFormValues) => Promise<void>
}

function CreateOfferDialog({
  open,
  onOpenChange,
  onSubmit,
}: CreateOfferDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)

  const form = useForm<CreateOfferFormValues>({
    resolver: zodResolver(createOfferSchema),
    defaultValues: {
      startDate: '',
      validUntil: '',
      templateContent: '',
    },
  })

  const handleSubmit = async (data: CreateOfferFormValues) => {
    setIsSubmitting(true)
    try {
      await onSubmit(data)
      form.reset()
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[450px]">
        <DialogHeader>
          <DialogTitle>Create Offer Letter</DialogTitle>
          <DialogDescription>Create an offer for this applicant</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="salary"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Salary *</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      placeholder="60000"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) =>
                        field.onChange(e.target.value ? Number(e.target.value) : undefined)
                      }
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Start Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="validUntil"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Valid Until</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="templateContent"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Template Content</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Optional offer letter content..."
                      rows={4}
                      {...field}
                      value={field.value ?? ''}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Creating...' : 'Create Offer'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
